import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BUILTIN_CATEGORY_KEYS } from "@/features/messages/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArrowCounterClockwiseIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import type { CustomFieldProps } from "./dynamic-form";

type Category = { key: string; label: string; description?: string };

/** The catch-all: the review files there whatever fits nothing else. */
const OTHER = "other";

const DEFAULTS: Category[] = BUILTIN_CATEGORY_KEYS.map((key) => ({
  key,
  label: "",
  description: "",
}));

const isBuiltin = (key: string) =>
  (BUILTIN_CATEGORY_KEYS as readonly string[]).includes(key);

/**
 * A key nobody reads, given once when a category is added and never again: a row
 * stores the key, so renaming the label later keeps every request filed there.
 */
const newKey = () =>
  `c_${Math.random().toString(36).slice(2, 10).padEnd(8, "0")}`;

/**
 * The Messagerie's categories (`messages.categories`): the families the AI files
 * requests under. Unset is the five built-in ones, shown under their translated
 * names; editing any of them saves the whole list. "Autre" cannot be removed —
 * a request that fits nothing has to land somewhere.
 *
 * A company setting only: one inbox, one list. An agent's own list would file the
 * same kind of request under two names depending on who took the call.
 */
export function MessageCategoriesField({
  fieldName,
  fieldSchema,
  value,
  onChange,
  inheritable,
}: CustomFieldProps) {
  const { t } = useTranslation("tools");
  const { t: tMessages } = useTranslation("messages");
  const base = "tools.catalog.messages.fields.categories";
  const label = fieldSchema.title || fieldName;

  if (inheritable) {
    return (
      <div className="space-y-1">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">
          {t(`${base}.companyOnly`)}
        </p>
      </div>
    );
  }

  const configured = Array.isArray(value) && value.length > 0;
  const categories: Category[] = configured ? (value as Category[]) : DEFAULTS;

  const update = (index: number, patch: Partial<Category>) =>
    onChange(
      categories.map((category, i) =>
        i === index ? { ...category, ...patch } : category,
      ),
    );

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {fieldSchema.description && (
        <p className="text-sm text-muted-foreground">
          {fieldSchema.description}
        </p>
      )}

      <div className="space-y-2 border-l-2 border-border pl-4">
        {categories.map((category, index) => (
          <div key={category.key} className="flex items-start gap-2">
            <div className="grid flex-1 gap-2 sm:grid-cols-[1fr_2fr]">
              <Input
                value={category.label}
                maxLength={60}
                aria-label={t(`${base}.label`)}
                placeholder={
                  isBuiltin(category.key)
                    ? tMessages(`categories.${category.key}`)
                    : t(`${base}.labelPlaceholder`)
                }
                // A category of the tenant's own needs a name; a built-in one
                // reads as its translated name when left empty.
                required={!isBuiltin(category.key)}
                onChange={(e) => update(index, { label: e.target.value })}
              />
              <Input
                value={category.description || ""}
                maxLength={300}
                aria-label={t(`${base}.description_field`)}
                placeholder={t(`${base}.descriptionPlaceholder`)}
                onChange={(e) => update(index, { description: e.target.value })}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={category.key === OTHER}
              title={
                category.key === OTHER ? t(`${base}.otherKept`) : undefined
              }
              aria-label={t(`${base}.remove`)}
              onClick={() => onChange(categories.filter((_, i) => i !== index))}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={categories.length >= 20}
          onClick={() =>
            onChange([
              ...categories,
              { key: newKey(), label: "", description: "" },
            ])
          }
        >
          <PlusIcon className="mr-2 h-4 w-4" />
          {t(`${base}.add`)}
        </Button>
        {configured && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange([])}
          >
            <ArrowCounterClockwiseIcon className="mr-2 h-4 w-4" />
            {t(`${base}.reset`)}
          </Button>
        )}
      </div>
    </div>
  );
}
